import { NgZone } from '@angular/core';
import { EncChannel, EncField, Encoding } from '@mode-vegas/vegas';
import { SelectedChartService } from '@mode/charts/contract';
import { TableEncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsService } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { Observable, Subject, interval } from 'rxjs';
import { filter, first, map, takeUntil, throttle, withLatestFrom } from 'rxjs/operators';
import { AppStore } from '~/types/main.types';
import { MixableBaseController } from '../../../../common/component';
import { ChartObservables } from '../../../../types/viz-designer.types';
import { DropZone, TableDropZone } from './builder-helpers';

class TableEncodingBuilderController extends MixableBaseController {
  static $inject = ['RxStoreService', 'ToastService', 'AnalyticsService', 'NgZone'];

  private dragCounter: number;
  private dragHovered: EncChannel | 'field-list' | null;

  private observables: ChartObservables;
  private model: FlamingoTypes.ModeModel;
  private encoding: Encoding;
  private format: ChartTypes.ChartFormat;

  private tableZone: DropZone;
  private queryToken$: Observable<string>;
  private hasAttrs$: Observable<boolean>;
  private chartService: SelectedChartService;
  private view$: Observable<FlamingoTypes.ModeView>;
  private dropIndex = -1;

  destroyed$ = new Subject<void>();

  constructor(
    private rxStore: AppStore,
    private alertService: ToastService,
    private analyticsService: AnalyticsService,
    private ngZone: NgZone
  ) {
    super();
  }

  $onInit() {
    this.dragCounter = 0;

    this.queryToken$ = this.observables.queryToken$;
    this.view$ = this.observables.view$;
    this.hasAttrs$ = this.observables.view$.pipe(
      filter((view) => view != null),
      map((view) => view.attrs.length > 0)
    );

    this.tableZone = new TableDropZone(
      'Table',
      'text',
      this.chartService,
      this.alertService,
      this.analyticsService,
      this.observables.chartType$,
      this.destroyed$,
      undefined,
      undefined,
      (field) => this.removeField(field),
      'table-encoding-builder .drop-area ul'
    );
    this.subscribeToDropIndexStream();
  }

  $onDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  removeField($field: EncField) {
    const encResult = TableEncodingHelpers.removeField(this.encoding, $field.id);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  clearAllColumns() {
    const encResult = TableEncodingHelpers.clearColumns(this.encoding);
    this.chartService.dispatchEncodingUpdate(encResult);
  }

  addRemainingColumns() {
    this.queryToken$
      .pipe(
        first(),
        withLatestFrom(this.hasAttrs$),
        filter(([, hasAttrs]) => hasAttrs),
        map(([queryToken]) => queryToken)
      )
      .subscribe((queryToken) => {
        const encResult = TableEncodingHelpers.addRemainingColumns(this.encoding, this.model, queryToken);
        if (encResult != null) {
          this.chartService.dispatchEncodingUpdate(encResult);
        }
      });
  }

  onAttrSelected($event) {
    const { event, attr } = $event;
    const newEncoding = this.tableZone.addTableField(this.encoding, attr, undefined);

    if (newEncoding != null) {
      this.chartService.dispatchEncodingUpdate(newEncoding);
    }
  }

  get tableFields() {
    return (this.encoding && this.encoding.marks.properties.text.values) || [];
  }

  get hasFields() {
    return this.tableFields.length > 0;
  }

  removeSort($source: string) {
    this.chartService.removeSort($source);
  }

  subscribeToDropIndexStream() {
    /**
     * this particular configuration of throttle helps to stream line values of
     * dropIndex as it resets to -1 several times during the drag
     */
    this.tableZone.dropIndexStream$
      .pipe(
        takeUntil(this.destroyed$),
        throttle((index: number) => interval(index === -1 ? 0 : 100), {
          leading: true,
          trailing: true,
        })
      )
      .subscribe((index: number) => {
        /**
         * Changes in dropIndex value present inside DropZone class were not reflecting
         * in DOM until dragOver event takes a break. So using ngZone here to update it on the go
         */
        this.ngZone.run(() => {
          this.dropIndex = index;
        });
      });
  }
}

export const TableEncodingBuilderComponent: angular.IComponentOptions = {
  bindings: {
    fieldDrop: '&',
    onTriggerEditFilter: '&',
    onUpdate: '&',
    onFieldDrag: '&',
    onFieldRemoved: '&',
    onTypeChanged: '&',
    showPalettesUpsell: '<',
    model: '<',
    encoding: '<',
    observables: '<',
    format: '<',
    chartService: '<',
  },
  controller: TableEncodingBuilderController,
  controllerAs: 'ctrl',
  template: `
  <!-- Column -->
  <div
    id="text"
    class="card-item"
    class="table-drop-zone-container"
    ng-dragenter="ctrl.tableZone.dragEnter($event)"
    ng-dragover="ctrl.tableZone.dragOver($event)"
    ng-dragleave="ctrl.tableZone.dragLeave($event)"
    ng-dragend="ctrl.tableZone.dragLeave($event)"
    ng-drop="ctrl.tableZone.fieldDropped($event, null, ctrl.encoding)"
  >
  <div class="header">
    Columns
    <div class="pull-right">
      <a class="link" ng-class="{ disabled: !(ctrl.hasAttrs$ | async:this) }" ng-click="ctrl.addRemainingColumns()">Add all</a>
      <span class="divider">|</span>
      <a class="link" ng-class="{ disabled: !ctrl.hasFields }" ng-click="ctrl.clearAllColumns()">Clear</a>
    </div>
  </div>
  <div class="drop-area" ng-class="{'drag-over': ctrl.dropIndex !== -1}">
    <ul class="list-unstyled">
      <li ng-repeat="field in ctrl.tableFields track by $index" data-index="{{ $index }}">
        <div ng-if="( ctrl.dropIndex ) === $index" class="drag-placeholder-field">
          <div class="cursor-left"></div>
          <div class="cursor-right"></div>
        </div>
        <table-field
          on-update="ctrl.fieldUpdated($result)"
          on-remove="ctrl.removeField($field)"
          on-remove-sort="ctrl.removeSort($source)"
          field="field"
          model="ctrl.model"
          view-name="ctrl.queryToken$ | async:this"
          encoding="ctrl.encoding"
          ng-dragenter="ctrl.tableZone.dragEnter($event, field.id)"
          ng-dragover="ctrl.tableZone.dragOver($event)"
          ng-dragleave="ctrl.tableZone.dragLeave($event)"
          ng-dragend="ctrl.tableZone.dragLeave($event, field.id)"
          ng-drop="ctrl.tableZone.fieldDropped($event, null, ctrl.encoding, field.id)"
          on-drag-start="ctrl.tableZone.dragStart($event, $field, $valid)"
          on-drag-cancel="ctrl.tableZone.dragEnd($event, 'text', $field)"
          view="ctrl.view$ | async:this"
        />
        <div ng-if="( ctrl.dropIndex ) >= ctrl.tableFields.length && $last" class="drag-placeholder-field">
          <div class="cursor-left"></div>
          <div class="cursor-right"></div>
        </div>
      </li>
      <mode-typeahead-attrs-list
        ng-dragenter="ctrl.tableZone.dragEnter($event)"
        data-index="{{ ctrl.tableFields.length }}"
        [view]="ctrl.view$ | async:this"
        [chart-type]="ctrl.observables.chartType$ | async:this"
        placeholder-text="Add fields here..."
        (attr-selected)="ctrl.onAttrSelected($event)"
      ></mode-typeahead-attrs-list>
    </ul>
  </div>
</div>
`,
};
